import { useEffect, useRef } from 'react';

export default function useClickOutside(
  refs: React.RefObject<HTMLElement | null>[],
  onClose: () => void,
  enabled: boolean
): void {
  const refsRef = useRef(refs);
  const onCloseRef = useRef(onClose);

  refsRef.current = refs;
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!enabled) return undefined;

    const isInside = (target: Node | null): boolean => {
      if (!target) return false;
      return refsRef.current.some(
        (ref) => ref.current && ref.current.contains(target)
      );
    };

    const isInOverlay = (target: Node | null): boolean => {
      const element = target as Element | null;
      if (!element?.closest) return false;
      return !!element.closest('[role="dialog"], [role="alertdialog"]');
    };

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (isInside(target) || isInOverlay(target)) return;
      event.stopPropagation();
      event.preventDefault();
    };

    const handleClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (isInside(target)) return;
      if (!isInOverlay(target)) {
        event.stopPropagation();
        event.preventDefault();
      }
      onCloseRef.current();
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handlePointerDown, true);
      document.addEventListener('click', handleClick, true);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handlePointerDown, true);
      document.removeEventListener('click', handleClick, true);
    };
  }, [enabled]);
}
